import React from "react";
import { Phone, Mail, Clock, MapPin, ArrowUpRight } from "lucide-react";
import { useContactModal } from "@/components/site/ContactModalContext";

const PHONE = "07810620113";
const PHONE_DISPLAY = "07810 620113";
const EMAIL = "Michaelskierniewski@gmail.com";

const Contact = () => {
  const { open: openModal } = useContactModal();

  const handleEmailClick = (e) => {
    e.preventDefault();
    openModal();
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[#262321] hairline-t overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-25"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1618108487640-249b7e29d73e?crop=entropy&cs=srgb&fm=jpg&w=1600&q=70')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0"
        style={{ background: "rgba(26,25,24,0.85)" }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 grain-overlay" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-6">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#C89D54]" />
            <span className="overline">Get In Touch</span>
          </div>

          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#F4F0EB] tracking-tight leading-[1.05]">
            A free 20-minute
            <span className="block italic text-[#C89D54]">consultation.</span>
          </h2>

          <p className="mt-6 font-sans-body text-base md:text-lg text-[#A6A098] leading-relaxed max-w-xl">
            I offer a free 20-minute telephone or Zoom consultation with no
            obligation. This is your opportunity to explain your situation and
            decide if you would like me to help you.
          </p>

          <p className="mt-4 font-sans-body text-sm text-[#A6A098]">
            I usually respond within a few hours.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              data-testid="contact-cta-call"
              href={`tel:+44${PHONE.replace(/^0/, "")}`}
              className="btn-primary"
            >
              <Phone size={16} /> Call {PHONE_DISPLAY}
            </a>
            <button
              type="button"
              data-testid="contact-cta-email"
              onClick={handleEmailClick}
              className="btn-secondary"
            >
              <Mail size={16} /> Send an Email
            </button>
          </div>
        </div>

        <div className="lg:col-span-6 space-y-px bg-[#C89D54]/15">
          <a
            data-testid="contact-phone"
            href={`tel:+44${PHONE.replace(/^0/, "")}`}
            className="bg-[#1A1918] p-7 md:p-8 flex items-center gap-4 group hover:bg-[#2E2A28] transition-colors"
          >
            <div className="h-11 w-11 shrink-0 flex items-center justify-center border border-[#C89D54]/30 group-hover:border-[#C89D54] transition-colors">
              <Phone size={18} className="text-[#C89D54]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="overline mb-1">Phone</div>
              <div className="text-base text-[#F4F0EB] font-medium truncate">
                {PHONE_DISPLAY}
              </div>
            </div>
            <ArrowUpRight
              size={18}
              className="text-[#A6A098] group-hover:text-[#C89D54] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all shrink-0"
            />
          </a>

          <button
            type="button"
            data-testid="contact-email"
            onClick={handleEmailClick}
            className="bg-[#1A1918] p-7 md:p-8 flex items-center gap-4 group hover:bg-[#2E2A28] transition-colors text-left w-full"
          >
            <div className="h-11 w-11 shrink-0 flex items-center justify-center border border-[#C89D54]/30 group-hover:border-[#C89D54] transition-colors">
              <Mail size={18} className="text-[#C89D54]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="overline mb-1">Email</div>
              <div className="text-base text-[#F4F0EB] font-medium truncate">
                {EMAIL}
              </div>
            </div>
            <ArrowUpRight
              size={18}
              className="text-[#A6A098] group-hover:text-[#C89D54] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all shrink-0"
            />
          </button>

          <div
            data-testid="contact-hours"
            className="bg-[#1A1918] p-7 md:p-8"
          >
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 shrink-0 flex items-center justify-center border border-[#C89D54]/30">
                <Clock size={18} className="text-[#C89D54]" />
              </div>
              <div className="flex-1">
                <div className="overline mb-2">Office Hours</div>
                <ul className="space-y-1.5 text-sm text-[#F4F0EB]">
                  <li className="flex justify-between gap-4">
                    <span className="text-[#A6A098]">Monday – Friday</span>
                    <span>6:00 PM – 9:00 PM</span>
                  </li>
                  <li className="flex justify-between gap-4">
                    <span className="text-[#A6A098]">Saturday</span>
                    <span>10:00 AM – 4:00 PM</span>
                  </li>
                  <li className="flex justify-between gap-4">
                    <span className="text-[#A6A098]">Sunday</span>
                    <span>Closed</span>
                  </li>
                </ul>
                <p className="mt-4 text-xs text-[#A6A098] leading-relaxed italic">
                  Outside these hours, please send a message — I will reply as
                  soon as possible.
                </p>
              </div>
            </div>
          </div>

          <div
            data-testid="contact-location"
            className="bg-[#1A1918] p-7 md:p-8"
          >
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 shrink-0 flex items-center justify-center border border-[#C89D54]/30">
                <MapPin size={18} className="text-[#C89D54]" />
              </div>
              <div>
                <div className="overline mb-2">Location</div>
                <p className="text-sm text-[#F4F0EB]">
                  Based near Mansfield — covering Nottinghamshire, Derbyshire
                  and Sheffield Family Courts.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
